/**
 * "Patrolling Mode": map + ordered waypoint loop, wait time, rounds.
 * Backend mission relay: POST /api/maps/send-mission (maps.controller.js `sendMissonData`).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Stack,
  Select,
  MenuItem,
  IconButton,
  TextField,
  Button,
  Typography,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import RouteIcon from "@mui/icons-material/Route";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HomeIcon from "@mui/icons-material/Home";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import MapIcon from "@mui/icons-material/Map";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import CONFIG from "../../config/config";
import { apiFetch, withAuthErrorHandling } from "../../services/http";
import { usePatrolStatus } from "../../hooks/usePatrolStatus";
import { submitReview } from "../../services/reviews.api";
import PatrolCompletePhase from "./PatrolCompletePhase";
import { useRobotCharging } from "../../hooks/useRobotCharging";
import { useBoxedIn } from "../../hooks/useBoxedIn";
import BoxedInNotice from "../../components/delivery/BoxedInNotice";
import SectionCard from "../../components/delivery/SectionCard";
import ActionButtonRow from "../../components/delivery/ActionButtonRow";
import DeliveryLayout, {
  buildOperationCrumbs,
  PageHead,
} from "../../components/delivery/DeliveryLayout";
import { ChargingBlockedDialog } from "../../components/delivery/ChargingBlockedDialog";
import { getSelectSx, getSectionBoxSx, primaryBtnSx } from "../delivery/deliveryStyles";
import { tokens } from "../../theme";

const MISSION_URL = `${CONFIG.BASE_URL}/api/maps/send-mission`;
const CMD_PATROL_START = "cmd_patrol_start";
const CMD_PATROL_STOP = "cmd_patrol_stop";

// Numeric fields hold raw keystrokes as a string so a half-typed value stays
// as typed; NaN here means "not yet valid", which gates the Start button.
const parseNum = (raw) => (String(raw).trim() === "" ? NaN : Number(raw));
const parseInteger = (raw) => {
  const n = parseNum(raw);
  return Number.isInteger(n) ? n : NaN;
};

// Upper bound on a single patrol run, so a mistyped value can't commit the
// robot for days. Mirrored in the sendMission endpoint so no client can bypass it.
const MAX_ROUNDS = 200;

// Allow only characters that can form a non-negative number, so the field can
// never hold something unsendable. `allowDecimal` is false for rounds, which
// must be a whole number of laps.
const sanitizeNumeric = (raw, allowDecimal) => {
  const cleaned = String(raw).replace(allowDecimal ? /[^\d.]/g : /[^\d]/g, "");
  if (!allowDecimal) return cleaned;
  const [whole, ...rest] = cleaned.split(".");
  return rest.length ? `${whole}.${rest.join("")}` : whole;
};

// A robot's assignedMap arrives populated ({_id,name}) from v1 and as a bare
// ObjectId string from legacy endpoints — same reader RoomNumberPageAero uses.
const readMapId = (assignedMap) =>
  (typeof assignedMap === "object" ? assignedMap?._id : assignedMap) || null;

// Consecutive identical stops are a no-op leg (travel from a location to
// itself), so this gates Start alongside allWaypointsChosen. Checked against
// filled waypoints only — an unfilled row in between is discarded on
// dispatch, so it shouldn't shield two same-name rows from the check.
const hasConsecutiveDuplicateWaypoint = (waypoints) => {
  const filled = waypoints.filter((wp) => wp.name);
  return filled.some((wp, i) => i > 0 && wp.name === filled[i - 1].name);
};

// Every "is this run startable" rule in one place, so the component reads the
// answers rather than re-deriving them. `!isPatrolling` is a UI gate only:
// patrol_runner.py's start() treats a second START as "replace the running
// patrol", so a double-press silently restarted from round 1.
const getPatrolFormState = ({
  waypoints,
  waitTime,
  rounds,
  robot,
  isRobotCharging,
  isRobotConnected,
  isPatrolling,
  selectedMapId,
  submitting,
}) => {
  // Unfilled rows are discarded on dispatch, so this only needs one filled
  // waypoint rather than every row being filled.
  const allWaypointsChosen = waypoints.some((w) => w.name);
  const hasConsecutiveDuplicate = hasConsecutiveDuplicateWaypoint(waypoints);

  const waitTimeValue = parseNum(waitTime);
  const roundsValue = parseInteger(rounds);
  const waitTimeValid = Number.isFinite(waitTimeValue) && waitTimeValue >= 0;
  // At least one full lap, and no more than MAX_ROUNDS so a single run cannot
  // tie the robot up indefinitely.
  const roundsValid = Number.isFinite(roundsValue) && roundsValue >= 1 && roundsValue <= MAX_ROUNDS;

  const canStart =
    !!robot?.serialID &&
    !isRobotCharging &&
    isRobotConnected &&
    !isPatrolling &&
    !!selectedMapId &&
    allWaypointsChosen &&
    !hasConsecutiveDuplicate &&
    waitTimeValid &&
    roundsValid &&
    !submitting;

  return {
    allWaypointsChosen,
    hasConsecutiveDuplicate,
    waitTimeValue,
    roundsValue,
    waitTimeValid,
    roundsValid,
    canStart,
  };
};

// One-line patrol status. Every field is optional, so this degrades to the
// plainest accurate sentence rather than rendering "round undefined".
const describePatrolProgress = (patrolState) => {
  const round = patrolState?.current_round;
  const totalRounds = patrolState?.rounds ?? patrolState?.total_rounds;
  const point = patrolState?.current_point ?? patrolState?.current_target?.name;
  const parts = [];
  if (round != null && totalRounds != null) parts.push(`round ${round}/${totalRounds}`);
  if (point) parts.push(`heading to ${point}`);
  return parts.length ? `Patrol in progress · ${parts.join(" · ")}` : "Patrol in progress";
};

// A patrol paused because the robot gave up boxed in. Resume (on the robot's
// tablet) retries the same point rather than skipping it.
const describeBoxedInPause = (patrolState) => {
  const point = patrolState?.current_point ?? patrolState?.current_target?.name;
  const where = point ? ` to ${point}` : "";
  return `Patrol paused — couldn't get through${where}. Clear the way, then press Resume on the robot's tablet to retry ${point ?? "the same point"}.`;
};

const isBoxedInPause = (patrolState) =>
  String(patrolState?.state || "").toLowerCase() === "paused" && patrolState?.reason === "boxed_in";

// Readiness for a live patrol: a boxed-in give-up pause, or progress.
const describeLivePatrol = (patrolState) =>
  isBoxedInPause(patrolState)
    ? { readiness: "paused", readinessLabel: describeBoxedInPause(patrolState) }
    : { readiness: "active", readinessLabel: describePatrolProgress(patrolState) };

// Plain-language readiness copy above the action bar. Charging is checked
// first: a docked robot can be perfectly reachable, so the connection check
// below would otherwise report the wrong blocker.
const getReadinessState = ({
  hasSelectedRobot,
  isRobotConnected,
  isRobotCharging,
  isPatrolling,
  patrolState,
  selectedMapId,
  needsManualMap,
  allWaypointsChosen,
  hasConsecutiveDuplicate,
  waitTimeValid,
  roundsValid,
  waypoints,
}) => {
  if (!hasSelectedRobot) {
    return { readiness: "incomplete", readinessLabel: "Select a robot to load its assigned map" };
  }
  if (isRobotCharging) {
    return { readiness: "charging", readinessLabel: "Robot is charging — patrol unavailable" };
  }
  if (!isRobotConnected) {
    return {
      readiness: "blocked",
      readinessLabel: "Robot disconnected — waiting for it to reconnect",
    };
  }
  // A running patrol outranks the form-readiness messages below: report what
  // the robot is doing, not a form the user has already submitted.
  if (isPatrolling) return describeLivePatrol(patrolState);
  if (!selectedMapId) {
    return {
      readiness: "incomplete",
      readinessLabel: needsManualMap
        ? "No map assigned to this robot — choose one to continue"
        : "Loading the robot's assigned map…",
    };
  }
  if (!allWaypointsChosen || hasConsecutiveDuplicate) {
    return {
      readiness: "incomplete",
      readinessLabel: hasConsecutiveDuplicate
        ? "Fix the duplicate consecutive waypoint"
        : "Select at least one waypoint",
    };
  }
  if (!waitTimeValid || !roundsValid) {
    return {
      readiness: "incomplete",
      readinessLabel: `Enter a valid wait time and 1–${MAX_ROUNDS} rounds`,
    };
  }
  const filledWaypoints = waypoints.filter((w) => w.name);
  return {
    readiness: "ready",
    readinessLabel:
      filledWaypoints.length === 1
        ? `Ready: ${filledWaypoints[0].name}`
        : `Ready: ${filledWaypoints.length} waypoints`,
  };
};

// Waypoint options for a map, pulled out of the effect so its try/catch and
// parsing branches don't count toward PatrolPage's own cognitive complexity.
// Also carries the map's operator-pinned base (Settings → Return to Base), so
// the Stop dialog can offer "Return to Base" the way the admin portal does.
const fetchMapLocationsData = async (selectedMapId) => {
  try {
    const token = localStorage.getItem("portalToken");
    const res = await withAuthErrorHandling(() =>
      axios.get(`${CONFIG.BASE_URL}/api/maps/${selectedMapId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
    );
    const raw = res?.data?.mapCoordinate;
    const locations = raw
      ? raw.map((item) => {
          const name = Object.keys(item)[0];
          const [x, y, yaw] = item[name];
          return { name, coords: { x, y, yaw } };
        })
      : [];
    return { locations, returnToBaseLocation: res?.data?.returnToBaseLocation ?? null };
  } catch (err) {
    console.error("[PatrolPage] failed to load map locations:", err);
    return { locations: [], returnToBaseLocation: null };
  }
};

// Colour tokens per readiness state — mirrors SelectionPhase's readiness strip.
const READINESS_TONES = {
  ready: { bg: tokens.successSoft, border: tokens.success, text: tokens.success },
  // A live patrol reads as informational-active (accent), distinct from the
  // green "form is ready to submit" state it replaces.
  active: { bg: tokens.accentSoft, border: tokens.accent, text: tokens.accent },
  blocked: { bg: tokens.dangerSoft, border: tokens.danger, text: tokens.danger },
  charging: { bg: tokens.warningSoft, border: tokens.warning, text: tokens.warning },
  paused: { bg: tokens.warningSoft, border: tokens.warning, text: tokens.warning },
  incomplete: { bg: tokens.surface2, border: tokens.border, text: tokens.text2 },
};

/** Status strip above the action bar — green when ready, red when blocked. */
const ReadinessRow = ({ state, label }) => {
  const tone = READINESS_TONES[state] || READINESS_TONES.incomplete;
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1}
      sx={{
        p: 1.5,
        background: tone.bg,
        border: `1px solid ${tone.border}`,
        borderRadius: "10px",
      }}
    >
      <FiberManualRecordIcon
        sx={{
          fontSize: 10,
          flexShrink: 0,
          color: tone.text,
          // Only the live-patrol state pulses, so an active loop is
          // distinguishable at a glance from a static readiness message.
          ...(state === "active" && {
            animation: "patrolReadinessPulse 1.4s ease-in-out infinite",
            "@keyframes patrolReadinessPulse": {
              "0%, 100%": { opacity: 1 },
              "50%": { opacity: 0.3 },
            },
          }),
        }}
      />
      <Typography sx={{ fontSize: 12, fontWeight: 500, color: tone.text }}>{label}</Typography>
    </Stack>
  );
};

ReadinessRow.propTypes = { state: PropTypes.string, label: PropTypes.string };

/** Read-only display of the map assigned to the selected robot — the map is a
 *  property of the robot, so it is shown, not chosen, whenever one is
 *  assigned. Matches the delivery flow's treatment of the same field. */
const AssignedMapRow = ({ mapName }) => (
  <Stack
    direction="row"
    alignItems="center"
    gap={1}
    sx={{
      px: 1.5,
      py: 1,
      minHeight: 40,
      boxSizing: "border-box",
      background: tokens.accentSoft,
      border: `1px solid ${tokens.accent}`,
      borderRadius: "9px",
    }}
  >
    <MapIcon sx={{ fontSize: 16, flexShrink: 0, color: tokens.accent }} />
    <Typography
      noWrap
      title={mapName || ""}
      sx={{ fontSize: 13, fontWeight: 600, color: tokens.text }}
    >
      {mapName || "Loading assigned map…"}
    </Typography>
  </Stack>
);

AssignedMapRow.propTypes = { mapName: PropTypes.string };

/** Step 1 map control: a disabled placeholder before a robot is picked, a
 *  manual picker when the robot has no assigned map, otherwise the read-only
 *  AssignedMapRow. */
const MapSelectionControl = ({
  hasSelectedRobot,
  needsManualMap,
  mapOptions,
  selectedMapId,
  onChange,
  assignedMapName,
  disabled,
}) => {
  if (!hasSelectedRobot) {
    return (
      <Select size="small" displayEmpty disabled value="" sx={{ ...getSelectSx(), width: "100%" }}>
        <MenuItem value="" sx={{ fontSize: 13, color: tokens.text3 }}>
          Select a robot first
        </MenuItem>
      </Select>
    );
  }

  if (needsManualMap) {
    const sortedMapOptions = [...mapOptions].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, {
        sensitivity: "base",
        numeric: true,
      })
    );
    return (
      <Select
        size="small"
        displayEmpty
        fullWidth
        value={selectedMapId}
        onChange={onChange}
        disabled={disabled}
        sx={getSelectSx()}
      >
        <MenuItem value="" sx={{ fontSize: 13, color: tokens.text3 }}>
          — Choose a map —
        </MenuItem>
        {sortedMapOptions.map((m) => (
          <MenuItem key={m._id} value={m._id} sx={{ fontSize: 13 }}>
            {m.name}
          </MenuItem>
        ))}
      </Select>
    );
  }

  return <AssignedMapRow mapName={assignedMapName} />;
};

MapSelectionControl.propTypes = {
  hasSelectedRobot: PropTypes.bool,
  needsManualMap: PropTypes.bool,
  mapOptions: PropTypes.arrayOf(PropTypes.object),
  selectedMapId: PropTypes.string,
  onChange: PropTypes.func,
  assignedMapName: PropTypes.string,
  disabled: PropTypes.bool,
};

// One numbered waypoint picker row. Pulled out of the waypoints.map so its
// duplicate-of-previous branching doesn't count toward PatrolPage's own
// cognitive complexity.
const WaypointRow = ({
  wp,
  index,
  prevWaypoint,
  mapLocations,
  onChange,
  onRemove,
  removeDisabled,
  disabled,
}) => {
  const prevName = prevWaypoint?.name ?? null;
  const isDuplicateOfPrev = !!wp.name && wp.name === prevName;
  const sortedLocations = [...mapLocations].sort((a, b) =>
    (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base", numeric: true })
  );

  return (
    <Stack direction="row" alignItems="flex-start" gap={1}>
      <Avatar
        sx={{
          width: 26,
          height: 26,
          borderRadius: "7px",
          background: tokens.accent,
          color: "#fff",
          fontSize: 12,
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {index + 1}
      </Avatar>
      <Box flex={1} minWidth={0}>
        <Select
          size="small"
          displayEmpty
          fullWidth
          value={wp.name}
          onChange={(e) => onChange(wp.id, e.target.value)}
          error={isDuplicateOfPrev}
          disabled={disabled}
          sx={getSelectSx()}
        >
          <MenuItem value="" sx={{ fontSize: 13 }}>
            — Choose location —
          </MenuItem>
          {sortedLocations.map((loc) => (
            <MenuItem
              key={loc.name}
              value={loc.name}
              disabled={loc.name === prevName}
              sx={{ fontSize: 13 }}
            >
              {loc.name}
            </MenuItem>
          ))}
        </Select>
        {isDuplicateOfPrev && (
          <Typography sx={{ fontSize: 11, color: tokens.danger, mt: 0.5, ml: 0.5 }}>
            Same as the previous stop — choose a different point.
          </Typography>
        )}
      </Box>
      <IconButton
        size="small"
        onClick={() => onRemove(wp.id)}
        disabled={removeDisabled || disabled}
        aria-label={`Remove waypoint ${index + 1}`}
        sx={{
          width: 26,
          height: 26,
          borderRadius: "7px",
          background: tokens.dangerSoft,
          color: tokens.danger,
          flexShrink: 0,
          "&:disabled": { opacity: 0.35 },
        }}
      >
        <CloseIcon sx={{ fontSize: 13 }} />
      </IconButton>
    </Stack>
  );
};

WaypointRow.propTypes = {
  wp: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }).isRequired,
  index: PropTypes.number.isRequired,
  prevWaypoint: PropTypes.shape({ name: PropTypes.string }),
  mapLocations: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })),
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  removeDisabled: PropTypes.bool,
  disabled: PropTypes.bool,
};

/** Inline success/error banner under the form. */
const StatusMessage = ({ status }) => {
  if (!status) return null;
  const isSuccess = status.tone === "success";
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: "10px",
        background: isSuccess ? tokens.successSoft : tokens.dangerSoft,
        border: `1px solid ${isSuccess ? tokens.success : tokens.danger}`,
        color: isSuccess ? tokens.success : tokens.danger,
        fontSize: 12.5,
        fontWeight: 600,
      }}
    >
      {status.message}
    </Box>
  );
};

StatusMessage.propTypes = {
  status: PropTypes.shape({ tone: PropTypes.string, message: PropTypes.string }),
};

const PatrolPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Robot Details ▸ Operations ▸ "Set up patrol" passes its robot id in nav
  // state; resolved against `portalRobots` once that list loads (see below).
  const presetRobotId = location.state?.presetRobotId ?? null;
  const [portalRobots, setPortalRobots] = useState([]);
  const [robot, setRobot] = useState(null);
  const [mapsAndIds, setMapsAndIds] = useState([]);
  const [selectedMapId, setSelectedMapId] = useState("");
  const [mapLocations, setMapLocations] = useState([]);
  // Operator-pinned base for the map (Settings → Return to Base). Null when
  // unset, in which case the robot falls back to its own last known base.
  const [returnToBaseLocation, setReturnToBaseLocation] = useState(null);
  // Ordered patrol loop. `id` exists only as a React key — two rows may hold
  // the same (or an empty) name — and is never sent to the robot.
  const [waypoints, setWaypoints] = useState([{ id: 0, name: "" }]);
  const [waitTime, setWaitTime] = useState("5");
  const [rounds, setRounds] = useState("1");
  const [lastMission, setLastMission] = useState(null); // payload of the last successful Start, for Stop
  const {
    patrolState,
    isPatrolling,
    setIsPatrolling,
    isRobotConnected,
    batteryLevel,
    // How the last run ended, or null. `isPatrolling: false` alone cannot say
    // whether a run finished, was halted, failed, or never started.
    terminalState,
    clearTerminalState,
  } = usePatrolStatus(robot?.serialID);
  // Hard-blocks patrol while the robot is on the charger — same guard and
  // source as the dispatch delivery flow (SelectionPhase/RoomNumberPageAero).
  const isRobotCharging = useRobotCharging(robot?.serialID, robot);
  // Live boxed-in banner while a patrol leg is blocked (see utils/boxedIn.js).
  const { view: boxedIn } = useBoxedIn(robot?.serialID);
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Confirmation gate for Stop: the robot halts wherever it is, which is
  // destructive enough to warrant an extra step.
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  // Surface the block as soon as a charging robot is picked, rather than
  // leaving the user to work out why Start Patrol is greyed out.
  const [chargingDialogOpen, setChargingDialogOpen] = useState(false);
  useEffect(() => {
    if (robot?.serialID && isRobotCharging) setChargingDialogOpen(true);
  }, [robot?.serialID, isRobotCharging]);
  const nextWaypointId = useRef(1);
  // The scrolling waypoint list, and a one-shot flag saying the next render
  // added a row and should scroll it into view.
  const waypointListRef = useRef(null);
  const scrollNewWaypointIntoView = useRef(false);

  const resetWaypoints = useCallback(() => {
    setWaypoints([{ id: nextWaypointId.current++, name: "" }]);
  }, []);

  // Portal-assigned robots — same endpoint the dispatch delivery flow uses.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const token = localStorage.getItem("portalToken");
        const res = await apiFetch(`${CONFIG.BASE_URL}/api/portal/robots`, {
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        });
        const list = await res.json();
        const robots = Array.isArray(list) ? list : list.robots || list.data || [];
        if (alive) setPortalRobots(robots);
      } catch (err) {
        console.error("[PatrolPage] failed to load robots:", err);
        if (alive) setPortalRobots([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Adopt the preset robot once the portal robot list has loaded. Runs only
  // while nothing is selected yet, so it can never fight a manual choice (or
  // re-apply itself after the user switches robots via the dropdown).
  useEffect(() => {
    if (!presetRobotId || robot || portalRobots.length === 0) return;
    const match = portalRobots.find((r) => r._id === presetRobotId);
    if (match) setRobot(match);
  }, [presetRobotId, robot, portalRobots]);

  // Map list this portal user may see.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const token = localStorage.getItem("portalToken");
        const res = await withAuthErrorHandling(() =>
          axios.get(`${CONFIG.BASE_URL}/api/maps/map-ids`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        );
        if (alive) setMapsAndIds(Array.isArray(res?.data) ? res.data : []);
      } catch (err) {
        console.error("[PatrolPage] failed to load maps:", err);
        if (alive) setMapsAndIds([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // The robot's assigned map drives the flow, matched against the fetched list
  // — a deleted map leaves a dangling id, so an unmatched id means "no map".
  const assignedMapId = readMapId(robot?.assignedMap);
  const assignedMap = useMemo(
    () => (assignedMapId ? mapsAndIds.find((m) => String(m._id) === String(assignedMapId)) : null),
    [assignedMapId, mapsAndIds]
  );

  // The assigned map may be missing from `mapsAndIds` if that list is scoped
  // differently than the robot's venue; without this the locked row shows blank.
  const mapOptions = useMemo(() => {
    if (assignedMapId && !mapsAndIds.some((m) => String(m._id) === String(assignedMapId))) {
      return [...mapsAndIds, { _id: assignedMapId, name: "Assigned map" }];
    }
    return mapsAndIds;
  }, [mapsAndIds, assignedMapId]);

  const hasSelectedRobot = !!robot;
  // True only once we know the robot has no usable map — nothing assigned, or
  // the assigned map is gone. Gated on the list having loaded so the manual
  // picker can't flash during the fetch.
  const needsManualMap = hasSelectedRobot && mapsAndIds.length > 0 && !assignedMap;
  const assignedMapName = mapOptions.find((m) => String(m._id) === String(assignedMapId))?.name;

  // Adopt the robot's assigned map automatically — also reacts if the robot's
  // assignment changes while the page is open. An unassigned robot clears the
  // field so the manual picker takes over.
  useEffect(() => {
    setSelectedMapId(assignedMapId ? String(assignedMapId) : "");
  }, [robot?._id, assignedMapId]);

  // Waypoint options for the selected map.
  useEffect(() => {
    let alive = true;
    setIsPatrolling(false);
    if (!selectedMapId) {
      setMapLocations([]);
      setReturnToBaseLocation(null);
      resetWaypoints();
      return undefined;
    }
    (async () => {
      const { locations, returnToBaseLocation: base } = await fetchMapLocationsData(selectedMapId);
      if (!alive) return;
      setMapLocations(locations);
      setReturnToBaseLocation(base);
      resetWaypoints();
    })();
    return () => {
      alive = false;
    };
  }, [selectedMapId, resetWaypoints, setIsPatrolling]);

  const updateWaypoint = (id, name) =>
    setWaypoints((prev) => prev.map((w) => (w.id === id ? { ...w, name } : w)));
  // The list scrolls inside a capped region, so a row appended past the cap
  // lands out of sight. The effect below consumes this once the row renders.
  const addWaypoint = () => {
    setWaypoints((prev) => [...prev, { id: nextWaypointId.current++, name: "" }]);
    scrollNewWaypointIntoView.current = true;
  };
  const removeWaypoint = (id) => setWaypoints((prev) => prev.filter((w) => w.id !== id));

  useEffect(() => {
    if (!scrollNewWaypointIntoView.current) return;
    scrollNewWaypointIntoView.current = false;
    const list = waypointListRef.current;
    if (!list) return;
    list.scrollTo({ top: list.scrollHeight, behavior: "smooth" });
  }, [waypoints.length]);

  const {
    allWaypointsChosen,
    hasConsecutiveDuplicate,
    waitTimeValue,
    roundsValue,
    waitTimeValid,
    roundsValid,
    canStart,
  } = getPatrolFormState({
    waypoints,
    waitTime,
    rounds,
    robot,
    isRobotCharging,
    isRobotConnected,
    isPatrolling,
    selectedMapId,
    submitting,
  });

  const { readiness, readinessLabel } = getReadinessState({
    hasSelectedRobot,
    isRobotConnected,
    isRobotCharging,
    isPatrolling,
    patrolState,
    selectedMapId,
    needsManualMap,
    allWaypointsChosen,
    hasConsecutiveDuplicate,
    waitTimeValid,
    roundsValid,
    waypoints,
  });

  const buildLocations = useCallback(
    // `point_id` stays the positional index — it is the patrol-order
    // identifier the robot expects. Shape must match what
    // botriq-aoa-server's patrol_runner.py `_loc_to_target` reads: `value`
    // as an [x, y, theta] triple, not `locationCoordinate`/`locationName` —
    // those go unrecognised there and silently fall back to a zeroed target,
    // driving every waypoint to the same point (the map frame's origin)
    // instead of the selected location.
    () =>
      waypoints
        .filter(({ name }) => name)
        .map(({ name }, i) => {
          const loc = mapLocations.find((l) => l.name === name);
          return {
            point_id: String(i),
            name,
            value: loc ? [loc.coords.x, loc.coords.y, loc.coords.yaw] : null,
          };
        }),
    [waypoints, mapLocations]
  );

  const sendMission = async (command, locations, mapId) => {
    setSubmitting(true);
    setStatus(null);
    try {
      const payload = {
        serialID: robot.serialID,
        mapId,
        locations,
        waitTime: waitTimeValid ? waitTimeValue : 0,
        rounds: roundsValid ? roundsValue : 1,
        command,
      };
      const token = localStorage.getItem("portalToken");
      const res = await apiFetch(MISSION_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.error || "Failed to send command.");
      const isStart = command === CMD_PATROL_START;
      if (isStart) setLastMission(payload);
      setIsPatrolling(isStart);
      // No banner on Start: the readiness strip above already reports live
      // progress. Stop still confirms, since nothing else reports it.
      setStatus(isStart ? null : { tone: "success", message: "Patrol stopped." });
    } catch (err) {
      console.error("[PatrolPage] send-mission failed:", err);
      setStatus({ tone: "error", message: err.message || "Failed to send command." });
    } finally {
      setSubmitting(false);
    }
  };

  // Hard block: checked here as well as in canStart's disabled Start button,
  // so the guard holds even if this handler is reached another way.
  const handleStart = () => {
    if (isRobotCharging) {
      setChargingDialogOpen(true);
      return;
    }
    sendMission(CMD_PATROL_START, buildLocations(), selectedMapId);
  };
  const handleStop = () => {
    if (!lastMission) {
      sendMission(CMD_PATROL_STOP, buildLocations(), selectedMapId);
      return;
    }
    sendMission(CMD_PATROL_STOP, lastMission.locations, lastMission.mapId);
  };

  // Stop where the robot currently is — the plain "cancel" choice.
  const handleConfirmStopInPlace = () => {
    setCancelConfirmOpen(false);
    handleStop();
  };

  // Stop the patrol, then send it home to the base configured for this map
  // (same /api/back-to-base contract RoomNumberPageAero uses for deliveries).
  const handleConfirmReturnToBase = async () => {
    setCancelConfirmOpen(false);
    handleStop();
    try {
      const token = localStorage.getItem("portalToken");
      await apiFetch(`${CONFIG.BASE_URL}/api/back-to-base`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          data: {
            serialID: robot.serialID,
            ...(returnToBaseLocation && {
              x: returnToBaseLocation.x,
              y: returnToBaseLocation.y,
              yaw: returnToBaseLocation.yaw ?? returnToBaseLocation.theta ?? 0,
              name: returnToBaseLocation.name,
            }),
          },
        }),
      });
    } catch (err) {
      console.error("[PatrolPage] back-to-base failed:", err);
    }
  };

  /**
   * Persist the operator's rating of the finished patrol.
   *
   * `type: "patrol"` is load-bearing: without it the backend falls back to "the
   * most recently completed delivery for this serialID" and attaches the review
   * to an unrelated trip. Resolves true/false rather than throwing, so the
   * completion screen can keep the form open for a retry.
   */
  const handleRatePatrol = useCallback(
    async (rating, comment) => {
      try {
        await submitReview({
          serialID: robot?.serialID,
          rating,
          comment,
          type: "patrol",
          patrolSummary: {
            roundsCompleted: patrolState?.current_round ?? null,
            totalRounds: Number(rounds) || null,
            waypointCount: waypoints.filter((w) => w?.name).length,
            // An operator Stop is not the robot finishing its rounds, and the
            // stored review should not imply that it was.
            cancelled: terminalState !== "completed",
          },
        });
        return true;
      } catch (err) {
        console.error("[PatrolPage] submit review failed:", err);
        return false;
      }
    },
    [robot?.serialID, patrolState, rounds, waypoints, terminalState]
  );

  // Acknowledge the finished run and return to the setup form, keeping the
  // route so another patrol can be started straight away.
  const handleNewPatrol = useCallback(() => {
    clearTerminalState();
    setStatus(null);
  }, [clearTerminalState]);

  // Same trail DeliveryLayout draws, reused by the summary screen so "back"
  // leads to the same place from both.
  const crumbs = buildOperationCrumbs("Patrolling Mode", presetRobotId, robot);
  const backRoute = crumbs.at(-2).route;

  // Acknowledge and leave the page entirely.
  const handleDonePatrolling = useCallback(() => {
    clearTerminalState();
    navigate(backRoute);
  }, [clearTerminalState, navigate, backRoute]);

  // Numbered pins showing the patrol loop in order, the same shape
  // LiveRobotPositionViewer already draws for a delivery's preview stops.
  const previewLocations = waypoints
    .map((wp) => {
      const loc = mapLocations.find((l) => l.name === wp.name);
      return loc ? { x: loc.coords.x, y: loc.coords.y, name: loc.name } : null;
    })
    .filter(Boolean);

  // Selecting a different robot clears any in-flight patrol state for the
  // previous one — same reset the inline Select's onChange used to do.
  const handleRobotChange = (robotId) => {
    const selected = portalRobots.find((r) => r._id === robotId);
    setIsPatrolling(false);
    setRobot(selected || null);
  };

  // A finished run replaces the form rather than sitting above it: the setup
  // controls would otherwise invite a Start on stale state.
  if (terminalState) {
    return (
      <Box
        sx={{
          flex: "none",
          height: "100%",
          maxHeight: "100%",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflow: "hidden",
          p: { xs: 2, sm: 3 },
        }}
      >
        {/* Same header as the setup screen; back also acknowledges the run. */}
        <PageHead
          title="Patrolling Mode"
          icon="route"
          crumbs={crumbs}
          onBack={handleDonePatrolling}
          sx={{ flex: "none" }}
        />
        <PatrolCompletePhase
          outcome={terminalState}
          patrolState={patrolState}
          waypoints={waypoints}
          rounds={rounds}
          onRate={handleRatePatrol}
          onNewPatrol={handleNewPatrol}
          onDone={handleDonePatrolling}
          crumbs={crumbs}
        />
      </Box>
    );
  }

  return (
    <>
      {/* DeliveryLayout — the same page shell (PageHead with breadcrumb + back
        button, header actions row, map|panel content row) Dispatch Delivery
        uses, per the Option 6 mockup's "Full DeliveryLayout Parity". */}
      <DeliveryLayout
        title="Patrolling Mode"
        icon="route"
        fromRobotId={presetRobotId ?? undefined}
        selectedMap={selectedMapId ? { _id: selectedMapId } : null}
        previewLocations={previewLocations}
        robot={robot}
        batteryLevel={typeof batteryLevel === "number" ? batteryLevel : undefined}
        portalRobots={portalRobots}
        onRobotChange={handleRobotChange}
        isRobotConnected={isRobotConnected}
        rightPanel={
          <SectionCard
            icon={
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "10px",
                  background: "rgba(255,255,255,0.15)",
                }}
              >
                <RouteIcon sx={{ fontSize: 20, color: "#fff" }} />
              </Avatar>
            }
            title="Destination Selection"
            subtitle="Select map and patrol waypoints"
            headerDirection="row"
            sx={{ flex: 1, minHeight: 0 }}
          >
            {/* ── Step 1: The robot's assigned map ── */}
            <Box sx={getSectionBoxSx()}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: tokens.text, mb: 0.5 }}>
                Step 1: Assigned Map
              </Typography>
              <Typography sx={{ fontSize: 11, color: tokens.text3, mb: 1.25 }}>
                {needsManualMap
                  ? "This robot has no assigned map — choose one for this patrol"
                  : "Loaded automatically from the selected robot"}
              </Typography>
              <MapSelectionControl
                hasSelectedRobot={hasSelectedRobot}
                needsManualMap={needsManualMap}
                mapOptions={mapOptions}
                selectedMapId={selectedMapId}
                onChange={(e) => setSelectedMapId(e.target.value)}
                assignedMapName={assignedMapName}
                disabled={isPatrolling}
              />
            </Box>

            {/* ── Step 2: Waypoints ── */}
            {selectedMapId && (
              <Box sx={getSectionBoxSx()}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: tokens.text, mb: 0.5 }}>
                  Step 2: Choose Waypoints
                </Typography>
                <Stack
                  direction="row"
                  alignItems="baseline"
                  justifyContent="space-between"
                  gap={1}
                  sx={{ mb: 1.25 }}
                >
                  <Typography sx={{ fontSize: 11, color: tokens.text3 }}>
                    Build the patrol loop in order
                  </Typography>
                  {/* Count is the cheap orientation cue once the list scrolls
                    inside its own region and no longer shows every row. */}
                  <Typography
                    sx={{ fontSize: 11, fontWeight: 700, color: tokens.text3, flexShrink: 0 }}
                  >
                    {waypoints.length} {waypoints.length === 1 ? "stop" : "stops"}
                  </Typography>
                </Stack>
                {/* The list scrolls within the card rather than growing the
                  page — without the cap a long loop pushes "Start Patrol"
                  below the fold at xs/sm. maxHeight is a row-count budget
                  (~54px/row), so short loops never show a scrollbar. */}
                <Stack
                  ref={waypointListRef}
                  gap={1}
                  mb={1.25}
                  sx={{
                    maxHeight: { xs: 236, md: 324 },
                    overflowY: "auto",
                    // Room for the focus ring and the duplicate-warning text
                    // so neither is clipped by the scroll container's edge.
                    px: "2px",
                    py: "2px",
                    mx: "-2px",
                    scrollbarGutter: "stable",
                  }}
                >
                  {waypoints.map((wp, i) => (
                    <WaypointRow
                      key={wp.id}
                      wp={wp}
                      index={i}
                      prevWaypoint={waypoints[i - 1]}
                      mapLocations={mapLocations}
                      onChange={updateWaypoint}
                      onRemove={removeWaypoint}
                      removeDisabled={waypoints.length === 1}
                      disabled={isPatrolling}
                    />
                  ))}
                </Stack>
                <Button
                  size="small"
                  startIcon={<AddIcon sx={{ fontSize: "14px !important" }} />}
                  onClick={addWaypoint}
                  disabled={isPatrolling}
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 12,
                    color: tokens.accent,
                    background: tokens.accentSoft,
                    "&:disabled": { opacity: 0.4 },
                  }}
                >
                  Add Waypoint
                </Button>
              </Box>
            )}

            {/* ── Step 3: Patrol settings ── */}
            <Box sx={getSectionBoxSx()}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: tokens.text, mb: 0.5 }}>
                Step 3: Patrol Settings
              </Typography>
              <Typography sx={{ fontSize: 11, color: tokens.text3, mb: 1.25 }}>
                Pause time at each stop and how many loops to run
              </Typography>
              <Stack direction="row" gap={2}>
                <Box flex={1}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: tokens.text, mb: 1 }}>
                    Wait Time (seconds per stop)
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    value={waitTime}
                    onChange={(e) => setWaitTime(sanitizeNumeric(e.target.value, true))}
                    error={!waitTimeValid}
                    helperText={waitTimeValid ? " " : "Enter a wait time of 0 or more."}
                    inputProps={{
                      inputMode: "decimal",
                      "aria-label": "Wait time in seconds per stop",
                    }}
                  />
                </Box>
                <Box flex={1}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: tokens.text, mb: 1 }}>
                    Rounds
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    value={rounds}
                    onChange={(e) => setRounds(sanitizeNumeric(e.target.value, false))}
                    error={!roundsValid}
                    helperText={roundsValid ? " " : `Enter 1 to ${MAX_ROUNDS} rounds.`}
                    inputProps={{
                      inputMode: "numeric",
                      "aria-label": "Number of patrol rounds",
                      max: MAX_ROUNDS,
                    }}
                  />
                </Box>
              </Stack>
            </Box>

            {/* ── Readiness status indicator ── */}
            <ReadinessRow state={readiness} label={readinessLabel} />

            {isPatrolling && <BoxedInNotice view={boxedIn} />}

            <StatusMessage status={status} />

            {/* ── Start/Stop Patrol — inside the card body, matching
              Dispatch Delivery's ActionButtonRow placement (SelectionPhase),
              rather than a separate sticky bar below the card. */}
            <ActionButtonRow>
              <Button
                variant="contained"
                color="success"
                disabled={!canStart}
                onClick={handleStart}
                startIcon={<PlayArrowIcon sx={{ fontSize: "18px !important" }} />}
                sx={primaryBtnSx}
              >
                {isPatrolling ? "Patrolling…" : "Start Patrol"}
              </Button>
              {isPatrolling && (
                <Button
                  variant="outlined"
                  disabled={submitting}
                  onClick={() => setCancelConfirmOpen(true)}
                  startIcon={<StopIcon sx={{ fontSize: "18px !important" }} />}
                  sx={{
                    ...primaryBtnSx,
                    color: tokens.danger,
                    borderColor: tokens.danger,
                  }}
                >
                  Stop Patrol
                </Button>
              )}
            </ActionButtonRow>
          </SectionCard>
        }
      />

      {/* Stop confirmation — mirrors the admin portal and the delivery flow's
          NavigatingPhase, offering the same choice: stop where the robot is,
          or stop and send it home. Rendered as a sibling of DeliveryLayout,
          not its `children` — that slot is layout-styled for the panel
          column and isn't where an overlay dialog belongs. */}
      <Dialog
        open={cancelConfirmOpen}
        onClose={() => setCancelConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, color: tokens.warning }}>
          <CancelIcon sx={{ color: tokens.warning }} />
          Stop Patrol
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14 }}>
            This will stop the robot&apos;s current patrol. Should it stop where it is, or return to
            base?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: "wrap" }}>
          <Button
            onClick={() => setCancelConfirmOpen(false)}
            variant="outlined"
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: 13 }}
          >
            Keep Patrolling
          </Button>
          <Button
            onClick={handleConfirmStopInPlace}
            variant="contained"
            startIcon={<StopCircleIcon />}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 700,
              fontSize: 13,
              background: tokens.warning,
              color: "#fff",
              "&:hover": { background: tokens.warning },
            }}
          >
            Stop Here
          </Button>
          <Button
            onClick={handleConfirmReturnToBase}
            variant="contained"
            startIcon={<HomeIcon />}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 700,
              fontSize: 13,
              background: tokens.accent,
              color: "#fff",
              "&:hover": { background: tokens.accent },
            }}
          >
            Return to Base
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hard block — see ChargingBlockedDialog. */}
      <ChargingBlockedDialog
        open={chargingDialogOpen}
        onClose={() => setChargingDialogOpen(false)}
        actionLabel="sent on patrol"
      />
    </>
  );
};

export default PatrolPage;
